import { Action } from '@ngrx/store';
import { ObservableAction } from '../shared/actions/observable-action';
import {
    ListItemSetAction,
    ListMergeAction,
    ListSetAction,
    ListSetFetchingAction,
    ListSetFetchingMoreAction,
} from '../shared/list/list.actions';
import { SharedAction } from '../shared/actions/shared-action';
import { GROUP_BILLING_INVOICE, GROUP_BILLING_USER } from '@core/state/state-groups';
import {
    BILLING_CREATE,
    BILLING_DETAILS_START,
    BILLING_ESTIMATE_FETCH,
    BILLING_ESTIMATE_FETCH_ERROR,
    BILLING_ESTIMATE_SET,
    BILLING_FETCH,
    BILLING_FETCH_ERROR,
    BILLING_INVOICES_FETCH,
    BILLING_INVOICE_FETCH,
    BILLING_KEY_FETCH,
    BILLING_KEY_SET,
    BILLING_LOCATION_SUBSCRIPTION_UPDATE,
    BILLING_PAYMENT_START,
    BILLING_PLAN_CHANGE_START,
    BILLING_SET,
    BILLING_SUBSCRIBE,
    BILLING_SUBSCRIBE_START,
    BILLING_SUBSCRIPTION_CANCEL,
    BILLING_TRIAL_START,
    BILLING_UPDATE,
    BILLING_UPGRADE_REQUEST,
    BILLING_USAGE_REPORT_FETCH,
    BILLING_USAGE_REPORT_SET,
    BILLING_USERS_FETCH,
    BILLING_USERS_FETCH_MORE,
    BILLING_VIEW
} from '../action-types';
import { SubscriptionPlan } from '@core/models/billing/subscription-plan';
import { SubscriptionTier } from '@core/models/billing/subscription-tier';
import { SubscriptionInterval } from '@core/models/billing/subscription-interval';
import { BILLING_INVOICE_ENTITY, USER_ENTITY } from '@core/state/entity-types';

export class ViewBillingAction implements Action {
    readonly type = BILLING_VIEW;

    constructor(public payload?: any) {}
}

export class FetchKeyAction implements Action {
    readonly type = BILLING_KEY_FETCH;
}

export class SetKeyAction implements Action {
    readonly type = BILLING_KEY_SET;

    constructor(public payload: any) {}
}

export class FetchBillingAction implements Action {
    readonly type = BILLING_FETCH;
}

export class FetchBillingErrorAction implements Action {
    readonly type = BILLING_FETCH_ERROR;

    constructor(public payload?: any) {}
}

@ObservableAction()
export class UpdateBillingAction implements Action {
    readonly type = BILLING_UPDATE;

    constructor(public payload: any) {}
}

@ObservableAction()
export class CreateBillingAction implements Action {
    readonly type = BILLING_CREATE;

    constructor(public payload: any) {}
}

export class SetBillingAction implements Action {
    readonly type = BILLING_SET;

    constructor(public payload: any) {}
}

export class FetchEstimateAction implements Action {
    readonly type = BILLING_ESTIMATE_FETCH;
}

export class FetchEstimateErrorAction implements Action {
    readonly type = BILLING_ESTIMATE_FETCH_ERROR;

    constructor(public payload: any) {}
}

export class SetEstimateAction implements Action {
    readonly type = BILLING_ESTIMATE_SET;

    constructor(public payload: any) {}
}

@ObservableAction()
export class FetchInvoiceAction implements Action {
    readonly type = BILLING_INVOICE_FETCH;

    constructor(public payload: { id: string }) {}
}

@ObservableAction()
export class FetchInvoicesAction implements Action {
    readonly type = BILLING_INVOICES_FETCH;
}

export class SetInvoiceAction
    extends ListItemSetAction
    implements SharedAction {
    name = GROUP_BILLING_INVOICE;

    constructor(public override payload: any) {
        super(payload);
        payload.domain = BILLING_INVOICE_ENTITY;
    }
}

export class SetInvoicesAction extends ListSetAction implements SharedAction {
    name = GROUP_BILLING_INVOICE;

    constructor(public override payload: any) {
        super(payload);
        payload.domain = BILLING_INVOICE_ENTITY;
    }
}

export class SetInvoicesFetchingAction
    extends ListSetFetchingAction
    implements SharedAction {
    name = GROUP_BILLING_INVOICE;

    constructor(public override payload: any) {
        super(payload);
    }
}

export class FetchBillingUsersAction implements Action {
    readonly type = BILLING_USERS_FETCH;

    constructor(public payload?: any) {}
}

export class FetchMoreBillingUsersAction implements Action {
    readonly type = BILLING_USERS_FETCH_MORE;
}

export class SetBillingUsersAction
    extends ListSetAction
    implements SharedAction {
    name = GROUP_BILLING_USER;

    constructor(public override payload: any) {
        super(payload);
        payload.domain = USER_ENTITY;
    }
}

export class MergeBillingUsersAction
    extends ListMergeAction
    implements SharedAction {
    name = GROUP_BILLING_USER;

    constructor(public override payload: any) {
        super(payload);
        payload.domain = USER_ENTITY;
    }
}

export class SetBillingUsersFetchingAction
    extends ListSetFetchingAction
    implements SharedAction {
    name = GROUP_BILLING_USER;

    constructor(public override payload: any) {
        super(payload);
    }
}
export class SetBillingUsersFetchingMoreAction
    extends ListSetFetchingMoreAction
    implements SharedAction {
    name = GROUP_BILLING_USER;

    constructor(public override payload: any) {
        super(payload);
    }
}

@ObservableAction()
export class SubscribeAction implements Action {
    readonly type = BILLING_SUBSCRIBE;

    constructor(public payload: { planId: SubscriptionPlan }) {}
}

@ObservableAction()
export class StartSubscribeAction implements Action {
    readonly type = BILLING_SUBSCRIBE_START;

    constructor(
        public payload: {
            planId: SubscriptionPlan;
            tier: SubscriptionTier;
            showPlanDetails?: boolean;
            onlyBilling?: boolean;
            interval?: SubscriptionInterval;
        }
    ) {}
}

@ObservableAction()
export class RequestUpgradeAction implements Action {
    readonly type = BILLING_UPGRADE_REQUEST;

    constructor() {}
}

@ObservableAction()
export class StartPlanChangeAction implements Action {
    readonly type = BILLING_PLAN_CHANGE_START;

    constructor(
        public payload: {
            planId: SubscriptionPlan;
            tier: SubscriptionTier;
            interval: SubscriptionInterval;
        }
    ) {}
}

@ObservableAction()
export class StartTrialAction implements Action {
    readonly type = BILLING_TRIAL_START;
}

export class StartPaymentMethodAction implements Action {
    readonly type = BILLING_PAYMENT_START;
}

export class StartBillingDetailsAction implements Action {
    readonly type = BILLING_DETAILS_START;
}

@ObservableAction()
export class CancelSubscriptionAction implements Action {
    readonly type = BILLING_SUBSCRIPTION_CANCEL;

    constructor(public payload: { planId: SubscriptionPlan }) {}
}

export class FetchBillingReportAction implements Action {
    readonly type = BILLING_USAGE_REPORT_FETCH;

    constructor(public payload: { startMonth: string; endMonth: string }) {}
}

export class SetBillingReportAction implements Action {
    readonly type = BILLING_USAGE_REPORT_SET;

    constructor(public payload: any) {}
}

@ObservableAction()
export class UpdateBillingLocationSubscription implements Action {
    readonly type = BILLING_LOCATION_SUBSCRIPTION_UPDATE;

    constructor(public payload: { quantity: number }) {}
}
